import { useState, useEffect, useCallback, useRef } from 'react';

const PROCESSING_MESSAGES = [
  'Listening to your voice to capture every instruction...',
  'Studying every detail you pointed out...',
  'Pinpointing the exact moments that matter most...',
  'Identifying the key people and things you mentioned...',
  'Organizing everything into clickable chapters...',
  'Crafting the perfect title for this video...',
  "Writing a concise summary (so you don't have to)...",
  'Ensuring they get it right the first time...',
  'Eliminating that follow-up meeting...',
  'Highlighting the details that usually get missed...',
  'Saving you from typing that long, detailed email...',
  'Making sure you never repeat yourself...',
];

const CYCLE_DURATION = 5000; // 5 seconds per message
const FADE_DURATION = 300; // 300ms fade transition

interface UseProcessingTitlesResult {
  currentMessage: string;
  isVisible: boolean;
}

/**
 * Hook that cycles through processing messages with fade transitions.
 * Returns the current message and visibility state for CSS transitions.
 */
const useProcessingTitles = (isProcessing: boolean): UseProcessingTitlesResult => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cycleToNextMessage = useCallback(() => {
    // Fade out
    setIsVisible(false);

    // After fade out completes, change message and fade in.
    // Track the timeout so it can't fire after unmount (or after a re-cycle).
    if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    fadeTimeoutRef.current = setTimeout(() => {
      setMessageIndex((prev) => (prev + 1) % PROCESSING_MESSAGES.length);
      setIsVisible(true);
    }, FADE_DURATION);
  }, []);

  useEffect(() => {
    if (!isProcessing) {
      // Reset when not processing
      setMessageIndex(0);
      setIsVisible(true);
      return;
    }

    // Set up interval to cycle messages
    const intervalId = setInterval(cycleToNextMessage, CYCLE_DURATION);

    return () => {
      clearInterval(intervalId);
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
    };
  }, [isProcessing, cycleToNextMessage]);

  return {
    currentMessage: PROCESSING_MESSAGES[messageIndex],
    isVisible,
  };
};

export default useProcessingTitles;
